import { Component, Host, h, Prop, Element, Watch, State, Listen, Fragment } from '@stencil/core';
import { deepParse, DomService, StringService } from '../../services';
import { RcmImageProp } from '../rcm-image/rcm-image.types';
import { TrackingService } from '../../services/tracking';
import { TrackingObjectBuilder } from '../../services/tracking/tracking-object-builder';
import { Multilink } from '../types/link.types';

export type Video = {
  videoAsset: string;
  imageSrc: RcmImageProp;
  headline: string;
  duration: string;
  videoSrc: Multilink;
  date: string;
};

@Component({
  tag: 'rcm-video-four-blocks',
  styleUrl: 'rcm-video-four-blocks.scss',
  shadow: true,
})
export class RcmVideoFourBlocks {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) videos: string | Video[];

  @State() modalVideoSrc: string;

  @State() showModal = false;

  @State() isSmallDevice: boolean;

  modalVideoAsset: string;

  modalThumbnail: string;

  componentWillLoad() {
    if (DomService.isIOS()) {
      this.isSmallDevice = true;
    } else {
      DomService.fireWhenBelow('large', () => {
        this.isSmallDevice = true;
      });
      DomService.fireWhenAboveOrEqual('large', () => {
        this.isSmallDevice = false;
      });
    }

    if (typeof this.videos === 'string') {
      this.videos = deepParse(this.videos);
    }
  }

  @Watch('videos')
  updateTeasers() {
    if (typeof this.videos === 'string') {
      this.videos = deepParse(this.videos);
    }
  }

  @Listen('rcmModalShowToggle')
  test(event) {
    if (event.detail === false) {
      this.showModal = event.detail;
      this.modalVideoSrc = '';
    }
  }

  handleClick(video: Video) {
    TrackingService.push(
      new TrackingObjectBuilder()
        .forLinkClick({
          clickText: video.headline,
          clickUrl: video.videoSrc.src,
          parentComponent: 'rcm-video-four-blocks',
        })
        .build(),
    );

    this.modalVideoSrc = video.videoSrc.src;
    this.modalVideoAsset = video.videoAsset;
    this.modalThumbnail = video.imageSrc.src;
    this.showModal = true;
  }

  render() {
    const items = this.videos as Video[];
    const { locale } = DomService.getGlobalConfig();
    const dateLocale = locale ? `${locale}` : 'de';

    return (
      <Host>
        <rcm-container>
          <rcm-columns multipleRows>
            {items.map((video) => (
              <rcm-column spanMedium={6} spanXlarge={3} key={`video-${video.headline ?? Date.now()}`} class="video-block__container">
                <div class="image__container">
                  {this.isSmallDevice ? (
                    <rcm-video
                      videoSrc={video.videoSrc.src}
                      videoAsset={video.videoAsset}
                      thumbnail={video.imageSrc}
                      parentComponent="rcm-video-four-blocks"
                      duration={video.duration}
                      fullWidth
                      mobilePlayFullscreen
                    />
                  ) : (
                    <button class="desktop-video-button" onClick={() => this.handleClick(video)}>
                      {StringService.isValidImageSrc(video.imageSrc) && (
                        <Fragment>
                          <rcm-image fullHeight={true} src={video.imageSrc} overrideMaxWidth={800} />
                          <div class="duration">{video.duration}</div>
                        </Fragment>
                      )}
                    </button>
                  )}
                </div>
                <rcm-headline class="test" level={4} headline={video.headline} color="var(--colors-dark-base)" />
                <p class="date">
                  {new Date(video.date).toLocaleDateString(dateLocale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </rcm-column>
            ))}
          </rcm-columns>
        </rcm-container>
        <rcm-modal show={this.showModal} padding="0" withCloseIcon={true} scrollable={false} backgroundColor="transparent">
          {this.showModal && (
            <rcm-video
              class="video"
              autoplay
              fullWidth
              videoSrc={this.modalVideoSrc}
              videoAsset={this.modalVideoAsset}
              thumbnail={this.modalThumbnail}
              parentComponent="rcm-video-four-blocks"
            />
          )}
        </rcm-modal>
      </Host>
    );
  }
}
